import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Row,
  Space,
  Table,
  message,
  Card,
  Typography,
  Tooltip,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useColumnTable } from "./table/data.table";
import requets from "../../api";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReportCsv } from "../../api/service/report/reportCsv";
import { ReportExcel } from "../../api/service/report/reportExcel";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useAppAuth } from "../../context/use-app-auth";

const { Title } = Typography;

export default function Device() {
  const auth = useAppAuth();
  const req = useMemo(
    () => requets(auth.user?.access_token),
    [auth.user?.access_token],
  );
  const { t } = useTranslation("global");
  const [messageSend, contextHolder] = message.useMessage();
  const controlPanel = useSelector((state) => state.controlPanel);
  const navigate = useNavigate();
  const [data, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const getDevices = useCallback(
    (control) => {
      const { department, county, location, devEui } = control;
      setLoading(true);
      req
        .get("/device", { department, county, location, devEui })
        .then((response) => {
          const raw = response.data;
          const all = Array.isArray(raw) ? raw : raw?.data || [];
          setDataSource(all.filter((d) => d.isActive === true));
        })
        .catch(() => setDataSource([]))
        .finally(() => setLoading(false));
    },
    [req],
  );

  const handleDeleteEvent = useCallback(
    (key) => {
      req
        .delete(`/device/${key}`)
        .then((response) => {
          if (response.status === 200) {
            messageSend.success(t("device.sucessDeleted"));
            getDevices(controlPanel);
          }
        })
        .catch(() => messageSend.error(t("deviceEdit.error")));
    },
    [req, messageSend, t, controlPanel, getDevices],
  );

  const columns = useColumnTable(handleDeleteEvent);

  useEffect(() => {
    getDevices(controlPanel);
  }, [controlPanel]);

  const translate = useCallback(
    (rows) =>
      rows.map((item) => {
        const raw = {
          nameDevice: item.name,
          description: item.description,
          eui: item.devEui,
          storeName: item.storeName,
          countyName: item.countyName,
          departmentName: item.departmentName,
          application: item.application,
          bucket: item.bucket,
        };
        const out = {};
        for (const key in raw) {
          out[t(`device.tableColumn.${key}`)] = raw[key];
        }
        return out;
      }),
    [t],
  );

  const exportFileName = `${t("device.devicesReport")}-${dayjs().format("YYYY-MM-DD")}`;

  return (
    <div className="layout-content">
      <style>
        {`
        /* BOTONES CUADRADOS */
        .square-btn {
          width: 40px !important;
          height: 40px !important;
          padding: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 8px !important;
        }

        .square-btn .anticon {
          display: flex !important;
          align-items: center;
          justify-content: center;
        }

        .square-btn .anticon svg {
          margin: auto;
        }

        /* ESTILOS MODAL CONFIRMACIÓN */
        .ant-modal-confirm-btns {
          display: flex !important;
          justify-content: center !important;
          gap: 12px !important;
          margin-top: 16px !important;
          width: 100% !important;
        }

        .ant-modal-confirm-btns button {
          width: 40px !important;
          height: 40px !important;
          min-width: 40px !important;
          padding: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 6px !important;
          font-size: 14px !important;
          line-height: 1 !important;
        }

        /* BORDES DE COLORES PARA BOTONES */
        .btn-outline-blue {
          border-color: #1677ff !important;
        }
        .btn-outline-blue:hover {
          border-color: #4096ff !important;
          background-color: #e6f4ff !important;
        }

        .btn-outline-green {
          border-color: #52c41a !important;
        }
        .btn-outline-green:hover {
          border-color: #73d13d !important;
          background-color: #f6ffed !important;
        }

        /* NUEVO: ESTILO PARA BOTÓN ELIMINAR (ROJO) */
        .btn-outline-red {
          border-color: #ff4d4f !important;
          color: #ff4d4f !important;
        }
        .btn-outline-red:hover {
          border-color: #ff7875 !important;
          background-color: #fff1f0 !important;
          color: #ff7875 !important;
        }

        /* POPCONFIRM (BOTONES NO / SÍ) */
        .ant-popconfirm-buttons {
          display: flex !important;
          justify-content: center !important;
          gap: 12px !important;
          width: 100% !important;
          margin-top: 12px !important;
        }

        .ant-popconfirm-buttons .ant-btn {
          width: 40px !important;
          height: 40px !important;
          min-width: 40px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 8px !important;
        }
        `}
      </style>

      {contextHolder}
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col xs={24} className="mb-24">
          <Card
            bordered={false}
            title={
              <Title level={4} style={{ margin: 0 }}>
                {t("device.title")}
              </Title>
            }
            extra={
              <Tooltip title={t("device.reload")}>
                <Button
                  type="default"
                  className="square-btn"
                  icon={<ReloadOutlined />}
                  onClick={() => getDevices(controlPanel)}
                />
              </Tooltip>
            }
          >
            <Row justify="end" style={{ marginBottom: 16 }}>
              <Col>
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate("/device/new")}
                  >
                    {t("device.create")}
                  </Button>
                  <ReportExcel
                    dataSet={translate(data)}
                    fileName={exportFileName}
                  />
                  <ReportCsv
                    dataSet={translate(data)}
                    fileName={exportFileName}
                  />
                </Space>
              </Col>
            </Row>
            <Table
              columns={columns}
              dataSource={data}
              rowKey="devEui"
              loading={loading}
              scroll={{ x: "max-content" }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `${t("device.total")}: ${total}`,
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
